import { useState, useEffect, useMemo, useRef } from 'react';
import PerfumeCard from '../components/PerfumeCard';

const WA_NUM = '543401418735';
const RECARGO = 0.30;
const fmtARS = n => Math.round(Number(n)).toLocaleString('es-AR');

const C = {
  cream:  '#f4efe6',
  black:  '#1a1a1a',
  gold:   '#c9a96e',
  goldDk: '#8c6d3f',
  muted:  '#6b6b6b',
  line:   'rgba(26,26,26,0.1)',
  white:  '#ffffff',
};
const serif = "'Cormorant Garamond', Georgia, serif";
const sans  = "'DM Sans', 'Segoe UI', sans-serif";

// ─── NAV ────────────────────────────────────────────────────────────────────
function Nav({ cartCount, onCartOpen }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled ? 'rgba(244,239,230,0.97)' : 'transparent',
      backdropFilter: scrolled ? 'blur(14px)' : 'none',
      borderBottom: scrolled ? `1px solid ${C.line}` : 'none',
      transition: 'all 0.3s ease',
      padding: scrolled ? '12px 6vw' : '20px 6vw',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontFamily: sans,
    }}>
      <a href="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
        <span style={{ fontFamily: serif, fontSize: 22, fontWeight: 600, color: C.black, fontStyle: 'italic', letterSpacing: 0.5 }}>
          Essence Pure
        </span>
      </a>
      <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
        {[['Inicio', '#hero'], ['Catálogo', '#catalogo'], ['Decants', '#catalogo'], ['Novedades', '#novedades'], ['Contacto', '#footer']].map(([label, href]) => (
          <a key={label} href={href} style={{ fontSize: 13, color: C.muted, textDecoration: 'none', letterSpacing: 0.3, transition: 'color 0.2s' }}
            onMouseEnter={e => e.target.style.color = C.black}
            onMouseLeave={e => e.target.style.color = C.muted}
          >{label}</a>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
        {cartCount > 0 && (
          <button onClick={onCartOpen} style={{ background: 'none', border: `1px solid ${C.line}`, borderRadius: 20, padding: '7px 16px', cursor: 'pointer', fontSize: 12, color: C.black, fontFamily: sans, display: 'flex', alignItems: 'center', gap: 6 }}>
            🛒 {cartCount}
          </button>
        )}
        <a href={`https://wa.me/${WA_NUM}`} target="_blank" rel="noreferrer"
          style={{ background: C.black, color: C.white, padding: '9px 20px', borderRadius: 20, fontSize: 12, fontFamily: sans, fontWeight: 600, textDecoration: 'none', letterSpacing: 0.3 }}>
          WhatsApp
        </a>
      </div>
    </nav>
  );
}

// ─── HERO ────────────────────────────────────────────────────────────────────
function Hero({ totalPerfumes }) {
  return (
    <section id="hero" style={{ background: C.cream, minHeight: '100vh', display: 'flex', alignItems: 'center', padding: '120px 6vw 80px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '15%', left: '50%', width: 1, height: '70%', background: `linear-gradient(180deg, transparent, ${C.line}, transparent)` }} />
      <div style={{ position: 'absolute', bottom: '10%', right: '8%', width: 180, height: 180, borderRadius: '50%', border: `1px solid ${C.line}`, pointerEvents: 'none' }} />

      <div style={{ flex: 1, maxWidth: 560, zIndex: 1 }}>
        <div style={{ fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', color: C.gold, fontFamily: sans, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ display: 'inline-block', width: 24, height: 1, background: C.gold }} />
          Alta Perfumería Árabe
        </div>
        <h1 style={{ fontFamily: serif, fontSize: 'clamp(42px, 6vw, 80px)', fontWeight: 600, lineHeight: 1.05, color: C.black, margin: '0 0 28px', letterSpacing: '-1px' }}>
          Perfumes Árabes<br /><em style={{ color: C.gold, fontStyle: 'italic' }}>Exclusivos</em>
        </h1>
        <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.9, fontFamily: sans, margin: '0 0 44px', maxWidth: 400, fontWeight: 300 }}>
          {totalPerfumes || 71} fragancias importadas de origen árabe, auténticas y exclusivas.<br />
          Oud, ámbar, almizque — con opción de decants 5ml y 10ml.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a href="#catalogo" style={{ background: C.black, color: C.white, padding: '14px 32px', borderRadius: 30, fontSize: 13, fontFamily: sans, fontWeight: 600, textDecoration: 'none', letterSpacing: 0.5 }}>
            Ver Catálogo
          </a>
          <a href={`https://wa.me/${WA_NUM}`} target="_blank" rel="noreferrer"
            style={{ background: 'transparent', color: C.black, border: `1px solid rgba(26,26,26,0.25)`, padding: '14px 32px', borderRadius: 30, fontSize: 13, fontFamily: sans, textDecoration: 'none', letterSpacing: 0.5 }}>
            Consultar
          </a>
        </div>
        <div style={{ display: 'flex', gap: 36, marginTop: 56 }}>
          {[[totalPerfumes || '71', 'Fragancias'], ['5ml', 'Decants'], ['100%', 'Originales']].map(([n, l]) => (
            <div key={l}>
              <div style={{ fontFamily: serif, fontSize: 26, fontWeight: 700, color: C.black }}>{n}</div>
              <div style={{ fontSize: 10, color: C.muted, letterSpacing: 2, textTransform: 'uppercase', fontFamily: sans }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 420, position: 'relative', zIndex: 1 }}>
        <div style={{ position: 'absolute', width: 340, height: 340, borderRadius: '50%', background: `radial-gradient(circle, rgba(201,169,110,0.15) 0%, transparent 70%)` }} />
        <div style={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', border: `1px solid rgba(201,169,110,0.2)` }} />
        <img src="/logo.png" alt="Essence Pure"
          style={{ maxWidth: 280, maxHeight: 380, objectFit: 'contain', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 20px 40px rgba(201,169,110,0.15))' }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      </div>
    </section>
  );
}

// ─── MARQUEE ────────────────────────────────────────────────────────────────
function Marquee() {
  const text = 'PERFUMES ORIGINALES ✶ OUD · ÁMBAR · ALMIZQUE ✶ 100% ORIGINALES ✶ DECANTS DISPONIBLES ✶ ';
  return (
    <div style={{ background: C.black, overflow: 'hidden', padding: '15px 0' }}>
      <div style={{ display: 'flex', whiteSpace: 'nowrap', animation: 'marqueeScroll 32s linear infinite' }}>
        {[text, text, text].map((t, i) => (
          <span key={i} style={{ fontSize: 10, letterSpacing: 3.5, textTransform: 'uppercase', color: C.gold, fontFamily: sans, flexShrink: 0 }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

// ─── FILTRO PILL ─────────────────────────────────────────────────────────────
function Pill({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '7px 18px', borderRadius: 30, border: `1px solid ${active ? C.black : C.line}`,
      background: active ? C.black : 'transparent', color: active ? C.white : C.muted,
      fontSize: 12, fontFamily: sans, cursor: 'pointer', letterSpacing: 0.3,
      transition: 'all 0.2s', whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {label}
    </button>
  );
}

// ─── MODAL DETALLE ────────────────────────────────────────────────────────────
function PerfumeModal({ perfume, onClose, onAgregar }) {
  const precio = parseFloat(perfume.precio) || 0;
  const pf    = perfume.descuento > 0 ? Math.round(precio * (1 - perfume.descuento / 100)) : precio;
  const d5    = parseFloat(perfume.precio_decant_5ml)  || 0;
  const d10   = parseFloat(perfume.precio_decant_10ml) || 0;
  const tieneBottle = precio > 0 && perfume.stock > 0;

  const sizes = [
    { key: '100ml', label: '100ml', precio: pf,  disabled: !tieneBottle },
    ...(d5  > 0 ? [{ key: '5ml',  label: '5ml',  precio: d5,  disabled: false }] : []),
    ...(d10 > 0 ? [{ key: '10ml', label: '10ml', precio: d10, disabled: false }] : []),
  ];

  const defaultKey = (sizes.find(s => !s.disabled) || sizes[0])?.key || '100ml';
  const [selectedKey, setSelectedKey] = useState(defaultKey);
  const selected = sizes.find(s => s.key === selectedKey) || sizes[0];

  const waMsg = `Hola! Me interesa ${perfume.nombre} de ${perfume.marca}. ¿Está disponible?`;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: C.white, borderRadius: 20, zIndex: 301,
        width: 'min(680px, 92vw)', maxHeight: '88vh', overflowY: 'auto',
        fontFamily: sans,
      }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: `1px solid ${C.line}`, borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: 14, color: C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>✕</button>

        <div style={{ display: 'flex', gap: 0 }} className="lp-modal-inner">
          {/* Imagen */}
          <div className="lp-modal-img" style={{
            width: 260, flexShrink: 0,
            background: (() => {
              const f = (perfume.familia||'').toLowerCase();
              if (f.includes('masc') || f.includes('hombre')) return 'linear-gradient(160deg, #e8ddd0, #c9b898)';
              if (f.includes('fem')  || f.includes('mujer'))  return 'linear-gradient(160deg, #ede0d4, #d4b8a0)';
              return 'linear-gradient(160deg, #e2ddd4, #c4b898)';
            })(),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, borderRadius: '20px 0 0 20px',
          }}>
            <img src={perfume.imagen_url} alt={perfume.nombre}
              style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.12))' }} />
          </div>

          {/* Info */}
          <div style={{ flex: 1, padding: '32px 28px' }}>
            <div style={{ fontSize: 9, color: C.gold, letterSpacing: 3, textTransform: 'uppercase', fontFamily: sans, marginBottom: 8 }}>{perfume.marca}</div>
            <h2 style={{ fontFamily: serif, fontSize: 28, fontWeight: 600, color: C.black, margin: '0 0 6px', lineHeight: 1.2 }}>{perfume.nombre}</h2>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 16, letterSpacing: 0.3 }}>
              {perfume.familia}{perfume.origen ? ` · ${perfume.origen}` : ''}
            </div>

            {perfume.notas && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: C.gold, marginBottom: 6 }}>Notas</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>{perfume.notas}</div>
              </div>
            )}

            <div style={{ height: 1, background: C.line, margin: '16px 0' }} />

            {/* Selector de tamaño */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: C.gold, marginBottom: 10 }}>Elegí el tamaño</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {sizes.map(s => (
                  <button
                    key={s.key}
                    onClick={() => !s.disabled && setSelectedKey(s.key)}
                    style={{
                      padding: '9px 16px',
                      borderRadius: 10,
                      border: `1.5px solid ${selectedKey === s.key ? C.black : s.disabled ? 'rgba(26,26,26,0.1)' : C.line}`,
                      background: selectedKey === s.key ? C.black : s.disabled ? 'transparent' : C.cream,
                      color: selectedKey === s.key ? '#fff' : s.disabled ? 'rgba(26,26,26,0.25)' : C.black,
                      fontSize: 13, fontFamily: sans, fontWeight: selectedKey === s.key ? 600 : 400,
                      cursor: s.disabled ? 'not-allowed' : 'pointer',
                      opacity: s.disabled ? 0.4 : 1,
                      transition: 'all 0.15s',
                      textDecoration: s.disabled ? 'line-through' : 'none',
                    }}
                  >
                    {s.label} — ${fmtARS(s.precio)}
                  </button>
                ))}
              </div>
            </div>

            {/* Precio del tamaño seleccionado */}
            {selected && !selected.disabled && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span style={{ fontFamily: serif, fontSize: 30, fontWeight: 700, color: C.black }}>${fmtARS(selected.precio)}</span>
                  {selected.key === '100ml' && perfume.descuento > 0 && (
                    <span style={{ fontSize: 13, color: '#bbb', textDecoration: 'line-through' }}>${fmtARS(precio)}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Con tarjeta: ${fmtARS(Math.round(selected.precio * (1 + RECARGO)))}</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
              {selected && !selected.disabled && (
                <button
                  onClick={() => { onAgregar({ ...perfume, size: selected.key, precio: selected.precio }); onClose(); }}
                  style={{ background: C.black, color: C.white, padding: '12px 24px', borderRadius: 24, fontSize: 13, fontFamily: sans, fontWeight: 600, border: 'none', cursor: 'pointer', letterSpacing: 0.3 }}
                >
                  + Agregar al carrito
                </button>
              )}
              <a href={`https://wa.me/${WA_NUM}?text=${encodeURIComponent(waMsg)}`}
                target="_blank" rel="noreferrer"
                style={{ background: 'transparent', color: C.black, border: `1px solid rgba(26,26,26,0.2)`, padding: '12px 24px', borderRadius: 24, fontSize: 13, fontFamily: sans, textDecoration: 'none', letterSpacing: 0.3 }}>
                💬 Consultar
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── DIFERENCIAL ─────────────────────────────────────────────────────────────
function Diferencial() {
  const pillars = [
    { icon: '✶', title: '100% Originales', desc: 'Auténticas, sin imitaciones.' },
    { icon: '🧪', title: 'Decants disponibles', desc: 'Probá antes de comprar. 5ml y 10ml.' },
    { icon: '💬', title: 'Asesoramiento', desc: 'Te guiamos para encontrar tu fragancia.' },
    { icon: '📦', title: 'Envíos en la zona', desc: 'Entrega rápida y segura.' },
  ];
  return (
    <section style={{ background: C.black, padding: '88px 6vw' }}>
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <div style={{ fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', color: C.gold, fontFamily: sans, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <span style={{ display: 'inline-block', width: 20, height: 1, background: C.gold }} />
          Por qué elegirnos
          <span style={{ display: 'inline-block', width: 20, height: 1, background: C.gold }} />
        </div>
        <h2 style={{ fontFamily: serif, fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 600, color: '#fff', margin: 0 }}>Nuestra promesa</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', maxWidth: 1100, margin: '0 auto' }}>
        {pillars.map((p, i) => (
          <div key={p.title} style={{ borderTop: `1px solid rgba(201,169,110,0.25)`, borderLeft: i > 0 ? '1px solid rgba(201,169,110,0.1)' : 'none', padding: '36px 32px' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ fontSize: 26, marginBottom: 16 }}>{p.icon}</div>
            <div style={{ fontFamily: serif, fontSize: 21, fontWeight: 600, color: '#fff', marginBottom: 10 }}>{p.title}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', fontFamily: sans, lineHeight: 1.7 }}>{p.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── INSTAGRAM ───────────────────────────────────────────────────────────────
function Instagram() {
  const items = ['linear-gradient(135deg, #e8ddd0, #c9b898)', 'linear-gradient(135deg, #ede0d4, #d4b8a0)', 'linear-gradient(135deg, #e2ddd4, #c4b898)', 'linear-gradient(135deg, #f4ede0, #dcc89a)'];
  return (
    <section id="novedades" style={{ background: C.white, padding: '88px 6vw' }}>
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <div style={{ fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', color: C.gold, fontFamily: sans, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <span style={{ display: 'inline-block', width: 20, height: 1, background: C.gold }} />
          Seguinos
          <span style={{ display: 'inline-block', width: 20, height: 1, background: C.gold }} />
        </div>
        <a href="https://www.instagram.com/essencepurecp" target="_blank" rel="noreferrer"
          style={{ fontFamily: serif, fontSize: 'clamp(24px, 4vw, 38px)', fontWeight: 600, color: C.black, textDecoration: 'none', fontStyle: 'italic' }}>
          @essencepurecp
        </a>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, maxWidth: 880, margin: '0 auto' }}>
        {items.map((bg, i) => (
          <a key={i} href="https://www.instagram.com/essencepurecp" target="_blank" rel="noreferrer"
            style={{ display: 'block', aspectRatio: '1/1', background: bg, borderRadius: 10, textDecoration: 'none', position: 'relative', transition: 'transform 0.25s' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(0.97)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(140,109,63,0.4)', fontSize: 28 }}>✶</div>
          </a>
        ))}
      </div>
    </section>
  );
}

// ─── FOOTER ──────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer id="footer" style={{ background: C.black, padding: '64px 6vw 32px', fontFamily: sans }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 48, marginBottom: 56 }}>
        <div>
          <div style={{ fontFamily: serif, fontSize: 24, fontWeight: 600, color: '#fff', fontStyle: 'italic', marginBottom: 16 }}>Essence Pure</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 2 }}>Alta perfumería árabe.<br />Fragancias premium importadas y auténticas.</div>
        </div>
        <div>
          <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: C.gold, marginBottom: 20 }}>Contacto</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 2.4 }}>
            <div>📱 3401 64-4076</div>
            <div>📱 3401 41-8735</div>
            <a href="https://www.instagram.com/essencepurecp" target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>📷 @essencepurecp</a>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: C.gold, marginBottom: 20 }}>Métodos de pago</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 2.4 }}>
            <div>💵 Efectivo</div><div>🏦 Transferencia</div><div>💳 Tarjeta (+30% recargo)</div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: C.gold, marginBottom: 20 }}>Políticas</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 2.4 }}>
            <div>Cambios y devoluciones</div><div>Envíos dentro de la zona</div><div>Productos 100% originales</div>
          </div>
        </div>
      </div>
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 24, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>© 2025 Essence Pure. Todos los derechos reservados.</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: 1, textTransform: 'uppercase' }}>Alta Perfumería Árabe</div>
      </div>
    </footer>
  );
}

// ─── CART DRAWER ─────────────────────────────────────────────────────────────
function CartDrawer({ cart, onClose, onCheckout }) {
  const total = cart.reduce((s, i) => s + parseFloat(i.precio) * i.qty, 0);
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, background: '#fff', zIndex: 201, padding: '28px 24px', overflowY: 'auto', fontFamily: sans, boxShadow: '-8px 0 32px rgba(0,0,0,0.12)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <span style={{ fontFamily: serif, fontSize: 24, fontWeight: 600, color: C.black }}>Tu carrito</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: C.muted }}>✕</button>
        </div>
        {cart.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: C.muted, fontSize: 14 }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>🛒</div>Tu carrito está vacío.
          </div>
        ) : (
          <>
            {cart.map(item => (
              <div key={`${item.id}_${item.size || '100ml'}`} style={{ display: 'flex', gap: 14, marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${C.line}` }}>
                <div style={{ width: 64, height: 64, flexShrink: 0, background: C.cream, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  <img src={item.imagen_url} alt={item.nombre} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, color: C.gold, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 3 }}>{item.marca}</div>
                  <div style={{ fontFamily: serif, fontSize: 15, fontWeight: 600, color: C.black }}>{item.nombre}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{item.size || '100ml'} · ×{item.qty}</div>
                </div>
                <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.black, flexShrink: 0 }}>${fmtARS(parseFloat(item.precio) * item.qty)}</div>
              </div>
            ))}
            <div style={{ marginTop: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: C.muted }}>Subtotal</span>
                <span style={{ fontFamily: serif, fontSize: 22, fontWeight: 700, color: C.black }}>${fmtARS(total)}</span>
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 24, textAlign: 'right' }}>Con tarjeta: ${fmtARS(Math.round(total * 1.30))}</div>
              <button onClick={onCheckout} style={{ width: '100%', padding: '15px', background: C.black, color: '#fff', border: 'none', borderRadius: 26, fontSize: 13, fontFamily: sans, fontWeight: 600, cursor: 'pointer', letterSpacing: 0.5 }}>
                💬 Pedir por WhatsApp
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function Landing() {
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [modalPerfume, setModalPerfume] = useState(null);

  // Filtros del catálogo
  const [buscar, setBuscar] = useState('');
  const [filtroGenero, setFiltroGenero] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [orden, setOrden] = useState('default');

  useEffect(() => {
    fetch('/api/perfumes')
      .then(r => r.json())
      .then(data => { setPerfumes(data); setLoading(false); })
      .catch(() => setLoading(false));

    // Tracking de visita (una por sesión)
    const SID_KEY = 'ep_sid';
    let sid = sessionStorage.getItem(SID_KEY);
    fetch('/api/visitas', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sid || undefined }),
    })
      .then(r => r.json())
      .then(data => { if (data.session_id) sessionStorage.setItem(SID_KEY, data.session_id); sid = data.session_id; })
      .catch(() => {});

    // Heartbeat cada 30s para "online ahora"
    const hb = setInterval(() => {
      const s = sessionStorage.getItem(SID_KEY);
      if (s) fetch('/api/visitas/heartbeat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ session_id: s }) }).catch(() => {});
    }, 30000);
    return () => clearInterval(hb);
  }, []);

  const marcas = useMemo(() => [...new Set(perfumes.map(p => p.marca).filter(Boolean))].sort(), [perfumes]);

  const filtrados = useMemo(() => {
    let lista = perfumes.filter(p => {
      const hd = parseFloat(p.precio_decant_5ml) > 0 || parseFloat(p.precio_decant_10ml) > 0;
      if ((!p.stock || p.stock <= 0) && !hd) return false;
      const mt = !buscar || p.nombre.toLowerCase().includes(buscar.toLowerCase()) || (p.marca||'').toLowerCase().includes(buscar.toLowerCase());
      const mg = !filtroGenero || p.familia === filtroGenero;
      const mTipo = !filtroTipo
        || (filtroTipo === 'entero'        && parseFloat(p.precio) > 0 && !hd)
        || (filtroTipo === 'entero-decant' && parseFloat(p.precio) > 0 && hd)
        || (filtroTipo === 'decant'        && hd);
      return mt && mg && mTipo;
    });
    if (orden === 'price-asc')  lista = [...lista].sort((a, b) => parseFloat(a.precio) - parseFloat(b.precio));
    if (orden === 'price-desc') lista = [...lista].sort((a, b) => parseFloat(b.precio) - parseFloat(a.precio));
    return lista;
  }, [perfumes, buscar, filtroGenero, filtroTipo, orden]);

  const addToCart = p => {
    const item = { ...p, size: p.size || '100ml' };
    setCart(prev => {
      const key = `${item.id}_${item.size}`;
      const idx = prev.findIndex(i => `${i.id}_${i.size || '100ml'}` === key);
      if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], qty: next[idx].qty + 1 }; return next; }
      return [...prev, { ...item, qty: 1 }];
    });
  };

  const checkoutWA = () => {
    const lines = cart.map(i => `• ${i.nombre} (${i.size || '100ml'}) ×${i.qty} — $${fmtARS(parseFloat(i.precio) * i.qty)}`).join('\n');
    const total = cart.reduce((s, i) => s + parseFloat(i.precio) * i.qty, 0);
    window.open(`https://wa.me/${WA_NUM}?text=${encodeURIComponent(`Hola! Quiero hacer el siguiente pedido:\n\n${lines}\n\nTotal: $${fmtARS(total)}`)}`, '_blank');
  };

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const hayFiltros = buscar || filtroGenero || filtroTipo;

  return (
    <div style={{ fontFamily: sans, background: C.white }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        @keyframes marqueeScroll { from { transform: translateX(0); } to { transform: translateX(-33.333%); } }
        input[type="text"]:focus { outline: none; border-color: #c9a96e !important; }
        @media (max-width: 768px) {
          .lp-hero { flex-direction: column !important; }
          .lp-hero-img { display: none !important; }
          .lp-modal-inner { flex-direction: column !important; }
          .lp-modal-img { width: 100% !important; border-radius: 20px 20px 0 0 !important; min-height: 200px; }
        }
      `}</style>

      <Nav cartCount={cartCount} onCartOpen={() => setShowCart(true)} />
      <Hero totalPerfumes={perfumes.filter(p => p.stock > 0).length || null} />
      <Marquee />

      {/* ── CATÁLOGO COMPLETO ────────────────────────────────────────────── */}
      <section id="catalogo" style={{ background: C.cream, padding: '88px 6vw' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', color: C.gold, fontFamily: sans, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <span style={{ display: 'inline-block', width: 20, height: 1, background: C.gold }} />
            Catálogo
            <span style={{ display: 'inline-block', width: 20, height: 1, background: C.gold }} />
          </div>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 600, color: C.black, margin: '0 0 8px' }}>
            Nuestras fragancias
          </h2>
          <p style={{ fontSize: 14, color: C.muted, fontFamily: sans }}>
            {loading ? 'Cargando...' : `${filtrados.length} fragancia${filtrados.length !== 1 ? 's' : ''} disponible${filtrados.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        {/* Barra de búsqueda */}
        <div style={{ maxWidth: 480, margin: '0 auto 28px', position: 'relative' }}>
          <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: C.muted }}>🔍</span>
          <input
            type="text"
            placeholder="Buscar por nombre o marca..."
            value={buscar}
            onChange={e => setBuscar(e.target.value)}
            style={{
              width: '100%', padding: '12px 16px 12px 40px',
              border: `1px solid ${C.line}`, borderRadius: 30,
              fontSize: 13, fontFamily: sans, color: C.black,
              background: C.white, transition: 'border-color 0.2s',
            }}
          />
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: 28, marginBottom: 36, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'center' }}>
          {/* Género */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Pill label="Todos" active={!filtroGenero} onClick={() => setFiltroGenero('')} />
            <Pill label="Masculino" active={filtroGenero === 'Masculino'} onClick={() => setFiltroGenero(filtroGenero === 'Masculino' ? '' : 'Masculino')} />
            <Pill label="Femenino"  active={filtroGenero === 'Femenino'}  onClick={() => setFiltroGenero(filtroGenero === 'Femenino'  ? '' : 'Femenino')}  />
            <Pill label="Unisex"    active={filtroGenero === 'Unisex'}    onClick={() => setFiltroGenero(filtroGenero === 'Unisex'    ? '' : 'Unisex')}    />
          </div>

          <div style={{ width: 1, background: C.line, alignSelf: 'stretch' }} />

          {/* Tipo */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Pill label="🧪 Con Decant"  active={filtroTipo === 'entero-decant'} onClick={() => setFiltroTipo(filtroTipo === 'entero-decant' ? '' : 'entero-decant')} />
            <Pill label="Solo Decants"   active={filtroTipo === 'decant'}        onClick={() => setFiltroTipo(filtroTipo === 'decant'        ? '' : 'decant')}        />
            <Pill label="Precio ↑"       active={orden === 'price-asc'}          onClick={() => setOrden(orden === 'price-asc' ? 'default' : 'price-asc')}           />
            <Pill label="Precio ↓"       active={orden === 'price-desc'}         onClick={() => setOrden(orden === 'price-desc' ? 'default' : 'price-desc')}          />
          </div>
        </div>

        {/* Limpiar filtros */}
        {hayFiltros && (
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <button onClick={() => { setBuscar(''); setFiltroGenero(''); setFiltroTipo(''); setOrden('default'); }}
              style={{ background: 'none', border: 'none', color: C.gold, fontSize: 12, cursor: 'pointer', fontFamily: sans, letterSpacing: 0.5, textDecoration: 'underline' }}>
              Limpiar filtros
            </button>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: C.muted, fontFamily: sans }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✶</div>
            Cargando fragancias...
          </div>
        ) : filtrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, color: C.muted, fontFamily: sans }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
            No se encontraron fragancias con esos filtros.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 24 }}>
            {filtrados.map(p => (
              <PerfumeCard
                key={p.id}
                perfume={p}
                onVerDetalle={setModalPerfume}
                onAgregarCarrito={addToCart}
              />
            ))}
          </div>
        )}
      </section>

      <Diferencial />
      <Instagram />
      <Footer />

      {showCart && <CartDrawer cart={cart} onClose={() => setShowCart(false)} onCheckout={checkoutWA} />}
      {modalPerfume && <PerfumeModal perfume={modalPerfume} onClose={() => setModalPerfume(null)} onAgregar={addToCart} />}
    </div>
  );
}
